<template>
  <modal-view
    :title="formTitle"
    :custom-max-width="'760px'"
    @close-modal="$emit('close')"
  >
    <form class="space-y-6 px-6 pb-8" @submit.prevent="handleSubmit">
      <div class="space-y-4">
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
          >
            Internal Name *
          </label>
          <input
            v-model="form.name"
            type="text"
            placeholder="PRO"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxlength="64"
          />
        </div>

        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
          >
            Display Name *
          </label>
          <input
            v-model="form.display_name"
            type="text"
            placeholder="Professional Plan"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxlength="128"
          />
        </div>

        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
          >
            Description
          </label>
          <textarea
            v-model="form.description"
            rows="3"
            placeholder="Highlight what stores get with this plan..."
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          ></textarea>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Price Amount
            </label>
            <input
              v-model.number="form.price_amount"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g., 5000 for 5000 RWF"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Enter the actual price (e.g., 5000). Use 0 for free plans.
            </p>
          </div>

          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Currency
            </label>
            <input
              v-model="form.currency"
              type="text"
              maxlength="3"
              placeholder="RWF"
              class="w-full px-3 py-2 uppercase border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Billing Period (days)
            </label>
            <input
              v-model.number="form.billing_period_days"
              type="number"
              min="1"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Sort Order
            </label>
            <input
              v-model.number="form.sort_order"
              type="number"
              min="0"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
          >
            Features (JSON)
          </label>
          <textarea
            v-model="featuresInput"
            rows="5"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder='{"max_products": 5000, "analytics": true, "api_access": true}'
          ></textarea>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Provide valid JSON. Leave empty for an empty feature set.
          </p>
        </div>

        <div
          class="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3"
        >
          <div>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100">
              Plan Visibility
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Active plans are available for assignment and public listing.
            </p>
          </div>
          <label class="inline-flex items-center">
            <input
              v-model="form.is_active"
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Active
            </span>
          </label>
        </div>
      </div>

      <div
        v-if="localError || serverError"
        class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200"
      >
        {{ localError || serverError }}
      </div>

      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          type="submit"
          :disabled="loading"
          class="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
        >
          <svg
            v-if="loading"
            class="animate-spin h-4 w-4 mr-2 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          <span>{{ formButtonLabel }}</span>
        </button>
      </div>
    </form>
  </modal-view>
</template>

<script>
import { reactive, ref, watch, computed } from "vue";
import ModalView from "@/components/modals/ModalView.vue";

const defaultForm = () => ({
  name: "",
  display_name: "",
  description: "",
  price_amount: null,
  currency: "RWF",
  billing_period_days: 30,
  features: {},
  sort_order: 0,
  is_active: true,
});

export default {
  name: "PlanFormModal",
  components: {
    ModalView,
  },
  props: {
    plan: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    serverError: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "save"],
  setup(props, { emit }) {
    const form = reactive(defaultForm());
    const featuresInput = ref("");
    const localError = ref("");

    const hydrateFromPlan = (plan) => {
      Object.assign(form, defaultForm(), plan || {});
      
      featuresInput.value = plan?.features
        ? JSON.stringify(plan.features, null, 2)
        : "";
      localError.value = "";
    };

    watch(
      () => props.plan,
      (plan) => {
        hydrateFromPlan(plan);
      },
      { immediate: true }
    );

    const formTitle = computed(() =>
      props.plan ? "Edit Subscription Plan" : "Create Subscription Plan"
    );
    const formButtonLabel = computed(() =>
      props.plan ? "Save Changes" : "Create Plan"
    );

    const handleSubmit = () => {
      localError.value = "";

      if (!form.name?.trim() || !form.display_name?.trim()) {
        localError.value = "Plan name and display name are required.";
        return;
      }

      let parsedFeatures = {};
      if (featuresInput.value.trim()) {
        try {
          parsedFeatures = JSON.parse(featuresInput.value);
        } catch (error) {
          localError.value =
            'Features must be valid JSON. Example: {"analytics": true}';
          return;
        }
      }

      const payload = {
        ...form,
        price_amount:
          form.price_amount === null || form.price_amount === ""
            ? 0
            : Number(form.price_amount),
        billing_period_days: Number(form.billing_period_days) || 0,
        sort_order: Number(form.sort_order) || 0,
        features: parsedFeatures,
      };

      emit("save", payload);
    };

    return {
      form,
      featuresInput,
      localError,
      formTitle,
      formButtonLabel,
      handleSubmit,
    };
  },
};
</script>
